import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Wrench, Lock, Trash2, FileSignature } from "lucide-react";

function daysInMonth(year, month0) {
  return new Date(year, month0 + 1, 0).getDate();
}

function fmtMonth(d, lang) {
  return d.toLocaleDateString(lang === "en" ? "en-US" : "it-IT", { month: "long", year: "numeric" });
}

function isoDay(year, month0, day) {
  const m = String(month0 + 1).padStart(2, "0");
  const d = String(day).padStart(2, "0");
  return `${year}-${m}-${d}`;
}

function cellStatusOf(bookings, scooterId, dayIso) {
  const dayStart = `${dayIso}T00:00:00`;
  const dayEnd = `${dayIso}T23:59:59`;
  for (const b of bookings) {
    if (b.scooter_id !== scooterId) continue;
    if (b.start_at <= dayEnd && b.end_at >= dayStart) {
      return b;
    }
  }
  return null;
}

const STATUS_CLASS = {
  pagato: "bg-[#FFD700] hover:bg-[#FACC15] text-black",
  in_attesa: "bg-yellow-500/60 hover:bg-yellow-500 text-black",
  // Walk-in "admin_block" — blu per distinguerlo dall'online (Iter 40, richiesta admin daltonico).
  admin_block: "bg-blue-500 hover:bg-blue-400 text-white",
};

// Walk-in / desk cells use a blue glow so they are clearly distinguishable
// from the yellow "online paid" cells even for admins with color-vision issues.
const BANCO_CLASS = "bg-blue-500 hover:bg-blue-400 text-white shadow-[0_0_8px_rgba(59,130,246,0.5)]";

// Iter 42 — contracts that come from a converted booking (either online paid
// or walk-in block) get a distinct sky-blue "celestino" so the admin can
// see at a glance which cells are "already turned into a printed contract".
const BANCO_CONVERTED_CLASS = "bg-sky-300 hover:bg-sky-200 text-slate-900 shadow-[0_0_8px_rgba(125,211,252,0.7)]";

export default function FleetCalendar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [scooters, setScooters] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCell, setSelectedCell] = useState(null); // {scooter, day, dayIso, existing}
  const [blockForm, setBlockForm] = useState({ customer_name: "", note: "", end_day: "" });
  const [submitting, setSubmitting] = useState(false);

  const year = cursor.getFullYear();
  const month0 = cursor.getMonth();
  const days = useMemo(() => Array.from({ length: daysInMonth(year, month0) }, (_, i) => i + 1), [year, month0]);
  const monthStart = `${year}-${String(month0 + 1).padStart(2, "0")}-01T00:00:00`;
  const monthEnd = `${year}-${String(month0 + 1).padStart(2, "0")}-${String(daysInMonth(year, month0)).padStart(2, "0")}T23:59:59`;

  const load = async () => {
    setLoading(true);
    try {
      const [s, b] = await Promise.all([
        api.get("/scooters"),
        api.get("/bookings/admin/calendar", { params: { start: monthStart, end: monthEnd } }),
      ]);
      setScooters(s.data);
      setBookings(b.data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    let active = true;
    const _monthStart = `${year}-${String(month0 + 1).padStart(2, "0")}-01T00:00:00`;
    const _monthEnd = `${year}-${String(month0 + 1).padStart(2, "0")}-${String(daysInMonth(year, month0)).padStart(2, "0")}T23:59:59`;
    Promise.all([
      api.get("/scooters"),
      api.get("/bookings/admin/calendar", { params: { start: _monthStart, end: _monthEnd } }),
    ]).then(([s, b]) => {
      if (!active) return;
      setScooters(s.data);
      setBookings(b.data);
      setLoading(false);
    }).catch(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [year, month0]);

  const today = new Date();
  const todayIso = isoDay(today.getFullYear(), today.getMonth(), today.getDate());

  const onCellClick = (scooter, day) => {
    const dayIso = isoDay(year, month0, day);
    const existing = cellStatusOf(bookings, scooter.id, dayIso);
    setSelectedCell({ scooter, day, dayIso, existing });
    setBlockForm({ customer_name: "", note: "", end_day: String(day) });
  };

  const createBlock = async () => {
    if (!selectedCell) return;
    const endDay = parseInt(blockForm.end_day, 10) || selectedCell.day;
    // Block the whole working day: 09:00 → 20:00 (covers the office opening hours).
    const startIso = `${selectedCell.dayIso}T09:00:00`;
    const endIso = `${isoDay(year, month0, endDay)}T20:00:00`;
    setSubmitting(true);
    try {
      await api.post("/bookings/admin/manual-block", {
        scooter_id: selectedCell.scooter.id,
        start_at: startIso,
        end_at: endIso,
        customer_name: blockForm.customer_name || null,
        note: blockForm.note || null,
      });
      toast.success(i18n.language === "en" ? "Block created" : "Blocco creato");
      setSelectedCell(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  const removeBlock = async (id) => {
    setSubmitting(true);
    try {
      await api.delete(`/bookings/admin/manual-block/${id}`);
      toast.success(i18n.language === "en" ? "Block removed" : "Blocco rimosso");
      setSelectedCell(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  const goPrev = () => setCursor(new Date(year, month0 - 1, 1));
  const goNext = () => setCursor(new Date(year, month0 + 1, 1));
  const goToday = () => { const d = new Date(); d.setDate(1); setCursor(d); };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#FFD700] mb-2">Fleet Calendar</p>
          <h1 className="font-display text-4xl font-bold tracking-tight">
            {i18n.language === "en" ? "Fleet Calendar" : "Calendario Flotta"}
          </h1>
          <p className="text-zinc-400 mt-1 text-sm">
            {i18n.language === "en"
              ? "Click any cell to add an at-desk block (paying in-store)."
              : "Clicca una cella per aggiungere un blocco al banco (cliente che paga in sede)."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goPrev} className="bg-zinc-900 border-zinc-800 hover:bg-zinc-800" data-testid="calendar-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="px-4 py-2 rounded-md bg-zinc-900 border border-zinc-800 font-display font-bold min-w-[180px] text-center capitalize" data-testid="calendar-month-label">
            {fmtMonth(cursor, i18n.language)}
          </div>
          <Button variant="outline" size="sm" onClick={goNext} className="bg-zinc-900 border-zinc-800 hover:bg-zinc-800" data-testid="calendar-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToday} className="bg-zinc-900 border-zinc-800 hover:bg-zinc-800 text-xs">
            {i18n.language === "en" ? "Today" : "Oggi"}
          </Button>
        </div>
      </div>

      <Legend isEn={i18n.language === "en"} />

      <div className="border border-zinc-800 rounded-md overflow-x-auto" data-testid="calendar-grid">
        {loading ? (
          <div className="p-8 text-center text-zinc-500">{t("common.loading")}</div>
        ) : scooters.length === 0 ? (
          <div className="p-8 text-center text-zinc-500">{t("admin.no_scooters")}</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-zinc-900/50">
              <tr>
                <th className="sticky left-0 z-10 bg-zinc-900/95 text-left px-3 py-2 font-semibold text-zinc-400 uppercase tracking-widest min-w-[160px]">
                  {i18n.language === "en" ? "Scooter" : "Scooter"}
                </th>
                {days.map((d) => {
                  const dayIso = isoDay(year, month0, d);
                  const isToday = dayIso === todayIso;
                  const dt = new Date(year, month0, d);
                  const dow = dt.getDay();
                  const isWeekend = dow === 0 || dow === 6;
                  return (
                    <th key={d} className={`px-1 py-2 font-medium ${isToday ? "text-[#FFD700]" : isWeekend ? "text-zinc-500" : "text-zinc-400"}`}>
                      {d}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {scooters.map((s) => (
                <tr key={s.id} className="border-t border-zinc-800">
                  <td className="sticky left-0 z-10 bg-zinc-950/95 px-3 py-2">
                    <div className="font-medium text-zinc-100 truncate max-w-[150px]">{s.model}</div>
                    <div className="text-[10px] text-zinc-500 font-mono uppercase">{s.plate}</div>
                  </td>
                  {days.map((d) => {
                    const dayIso = isoDay(year, month0, d);
                    const b = cellStatusOf(bookings, s.id, dayIso);
                    const inMaint = s.status === "manutenzione";
                    const isBanco = b && (b.kind === "banco" || b.kind === "banco_block" || b.kind === "banco_converted");
                    const isBancoConverted = b && b.kind === "banco_converted";
                    // Iter 43 — bookings ALWAYS win over the maintenance overlay.
                    // Previously a vehicle currently in maintenance had ALL its
                    // future calendar cells painted red, hiding real July/August
                    // bookings and blocks. Now:
                    //   * cell with a booking/block/contract → booking color
                    //     + red outline if the vehicle is under maintenance (warn admin)
                    //   * cell with no booking + vehicle in maintenance → red overlay
                    //   * otherwise → green (available)
                    let cls;
                    if (b) {
                      const base = isBancoConverted ? BANCO_CONVERTED_CLASS
                        : isBanco ? BANCO_CLASS
                        : STATUS_CLASS[b.payment_status] || "bg-zinc-700";
                      cls = inMaint ? `${base} ring-2 ring-red-400/70` : base;
                    } else if (inMaint) {
                      cls = "bg-red-500/30 hover:bg-red-500/40";
                    } else {
                      cls = "bg-emerald-500/20 hover:bg-emerald-500/30";
                    }
                    const titleParts = [];
                    if (b) {
                      titleParts.push(isBancoConverted ? "Contratto (convertito)" : isBanco ? "Al banco" : b.payment_status);
                      if (b.manual_customer_name) titleParts.push(b.manual_customer_name);
                      titleParts.push(s.plate);
                      if (inMaint) titleParts.push("⚠ veicolo in manutenzione");
                    } else if (inMaint) titleParts.push("Manutenzione");
                    else titleParts.push(dayIso);
                    return (
                      <td key={d} className="p-0.5">
                        <button
                          type="button"
                          onClick={() => onCellClick(s, d)}
                          className={`w-7 h-7 rounded-sm transition-colors ${cls}`}
                          title={titleParts.join(" · ")}
                          data-testid={`cal-cell-${s.id}-${d}`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!selectedCell} onOpenChange={(o) => !o && setSelectedCell(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">
              {selectedCell?.scooter?.model} · {selectedCell?.dayIso}
            </DialogTitle>
          </DialogHeader>

          {selectedCell?.existing ? (
            <div className="space-y-3">
              <div className="text-sm">
                <div className="text-zinc-400">Status</div>
                <div className="font-semibold capitalize">
                  {selectedCell.existing.kind === "banco" ? (i18n.language === "en" ? "At-desk (manual contract)" : "Al banco (contratto)")
                    : selectedCell.existing.payment_status === "admin_block" ? (i18n.language === "en" ? "At-desk block" : "Blocco al banco")
                    : selectedCell.existing.payment_status.replace("_", " ")}
                </div>
              </div>
              <div className="text-sm">
                <div className="text-zinc-400">{i18n.language === "en" ? "From" : "Da"}</div>
                <div>{new Date(selectedCell.existing.start_at).toLocaleString()}</div>
              </div>
              <div className="text-sm">
                <div className="text-zinc-400">{i18n.language === "en" ? "To" : "A"}</div>
                <div>{new Date(selectedCell.existing.end_at).toLocaleString()}</div>
              </div>
              {selectedCell.existing.user_email && (
                <div className="text-sm">
                  <div className="text-zinc-400">{i18n.language === "en" ? "Customer" : "Cliente"}</div>
                  <div className="font-mono text-xs">{selectedCell.existing.manual_customer_name || selectedCell.existing.user_email}</div>
                </div>
              )}
              {selectedCell.existing.manual_note && (
                <div className="text-sm">
                  <div className="text-zinc-400">{i18n.language === "en" ? "Note" : "Nota"}</div>
                  <div className="text-xs">{selectedCell.existing.manual_note}</div>
                </div>
              )}
              {selectedCell.existing.payment_status === "admin_block" && !selectedCell.existing.converted_to_contract_id && (
                <>
                  <Button
                    onClick={() => {
                      setSelectedCell(null);
                      navigate(`/admin/contracts/new?from_booking=${selectedCell.existing.id}`);
                    }}
                    className="w-full bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold"
                    data-testid="convert-block-to-contract-button"
                  >
                    <FileSignature className="w-4 h-4 mr-2" />
                    {i18n.language === "en" ? "Convert to contract" : "Trasforma in contratto"}
                  </Button>
                  <Button variant="destructive" onClick={() => removeBlock(selectedCell.existing.id)} disabled={submitting} className="w-full" data-testid="remove-block-button">
                    <Trash2 className="w-4 h-4 mr-2" /> {i18n.language === "en" ? "Remove block" : "Rimuovi blocco"}
                  </Button>
                </>
              )}
              {selectedCell.existing.payment_status === "admin_block" && selectedCell.existing.converted_to_contract_id && (
                <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 px-3 py-2 text-sm text-emerald-300 flex items-center gap-2" data-testid="block-converted-hint">
                  <FileSignature className="w-4 h-4" />
                  {i18n.language === "en" ? "Already converted to contract" : "Già trasformato in contratto"}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-zinc-300">
                <Lock className="w-4 h-4 text-[#FFD700]" />
                {i18n.language === "en" ? "Block this scooter (at-desk customer)" : "Blocca scooter (cliente al banco)"}
              </div>
              {selectedCell?.scooter?.status === "manutenzione" && (
                <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200 flex items-start gap-2" data-testid="cal-maint-hint">
                  <Wrench className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>
                    {i18n.language === "en"
                      ? "Vehicle currently in maintenance. The block will be saved anyway — make sure the scooter will be ready by the start date."
                      : "Veicolo attualmente in manutenzione. Il blocco verrà salvato comunque — verifica che lo scooter sarà pronto per la data di inizio."}
                  </span>
                </div>
              )}
              <div>
                <Label className="text-zinc-300">{i18n.language === "en" ? "Until day" : "Fino al giorno"}</Label>
                <Input type="number" min={selectedCell?.day} max={daysInMonth(year, month0)}
                       value={blockForm.end_day}
                       onChange={(e) => setBlockForm({ ...blockForm, end_day: e.target.value })}
                       className="bg-zinc-950 border-zinc-800 mt-1.5"
                       data-testid="block-end-day-input" />
              </div>
              <div>
                <Label className="text-zinc-300">{i18n.language === "en" ? "Customer name (optional)" : "Nome cliente (opzionale)"}</Label>
                <Input value={blockForm.customer_name}
                       onChange={(e) => setBlockForm({ ...blockForm, customer_name: e.target.value })}
                       className="bg-zinc-950 border-zinc-800 mt-1.5"
                       data-testid="block-customer-input" />
              </div>
              <div>
                <Label className="text-zinc-300">{i18n.language === "en" ? "Note" : "Nota"}</Label>
                <Input value={blockForm.note}
                       onChange={(e) => setBlockForm({ ...blockForm, note: e.target.value })}
                       className="bg-zinc-950 border-zinc-800 mt-1.5"
                       data-testid="block-note-input" />
              </div>
              <DialogFooter>
                <Button onClick={createBlock} disabled={submitting} className="bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold w-full" data-testid="create-block-button">
                  {submitting ? "…" : (i18n.language === "en" ? "Create block" : "Crea blocco")}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Legend({ isEn }) {
  const items = [
    { color: "bg-emerald-500/20 border border-emerald-500/30", label: isEn ? "Available" : "Disponibile" },
    { color: "bg-[#FFD700]", label: isEn ? "Paid online" : "Pagato online" },
    { color: "bg-yellow-500/60", label: isEn ? "Pending" : "In attesa" },
    { color: "bg-blue-500 shadow-[0_0_6px_rgba(59,130,246,0.6)]", label: isEn ? "At-desk (banco)" : "Al banco" },
    { color: "bg-sky-300 shadow-[0_0_6px_rgba(125,211,252,0.7)]", label: isEn ? "Contract (converted)" : "Contratto (convertito)" },
    { color: "bg-red-500/30", label: isEn ? "Maintenance" : "Manutenzione" },
    { color: "bg-blue-500 ring-2 ring-red-400/70", label: isEn ? "Booking on maintenance vehicle" : "Prenotazione su veicolo in manutenzione" },
  ];
  return (
    <div className="flex flex-wrap gap-3 text-xs text-zinc-400">
      {items.map((i) => (
        <div key={i.label} className="flex items-center gap-1.5">
          <span className={`w-4 h-4 rounded-sm ${i.color}`} />
          <span>{i.label}</span>
        </div>
      ))}
    </div>
  );
}
